import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  communicationApiClient,
  onboardingApiClient,
  wxApiClient,
} from "@shared/api/clients/apiClientFactory";
import { toSnakeCase } from "@shared/utils/caseConverter";
import { PAGE_SIZE } from "@shared/utils/constants";

const isValidParam = (value: any) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};
export const getFieldForceSummary = async (remote_utility_id: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "wx",
    "field-force-summary",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
    }
  );

  const response = await wxApiClient.get(url);
  return response.data.result ?? [];
};

export const getFieldForceData = async (
  remote_utility_id: string,
  page: number,
  limit: number,
  search_data?: string,
  filters?: any
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "wx",
    "field-force",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("page", page);
      query.push("limit", limit);
      query.push("search_data", search_data);
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (!isValidParam(value)) return;
          query.push(toSnakeCase(key), value);
        });
      }
    }
  );

  const response = await wxApiClient.get(url);
  return response.data ?? [];
};

export const exportFieldForceData = async (
  remote_utility_id: string,
  page?: number,
  limit?: number,
  search_data?: string,
  filters?: any
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "wx",
    "field-force",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      if (page) query.push("page", page);
      if (limit) query.push("limit", limit);
      if (search_data) query.push("search_data", search_data);
      query.push("export_format", "csv");
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (!isValidParam(value)) return;
          query.push(toSnakeCase(key), value);
        });
      }
    }
  );

  const response = await wxApiClient.get(url);
  return response.data;
};

export const getFieldForceDetails = async (
  remote_utility_id: string,
  ff_no: string
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "wx",
    `get-field-force/${ff_no}`,
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
    }
  );

  const response = await wxApiClient.get(url);
  return response.data.result ?? [];
};

export const getFieldForceAssignments = async (
  remote_utility_id: string,
  ff_no: string,
  page: number = 1,
  search_data?: string,
  filters?: any
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "wx",
    `job-card/action`,
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("field_force_no", ff_no);
      query.push("page", page.toString());
      query.push("limit", PAGE_SIZE); // Add limit parameter

      // ✅ Add search parameter if provided
      if (search_data && search_data.trim()) {
        query.push("search_data", search_data);
      }
      Object.entries(filters).forEach(([key, value]) => {
        if (!isValidParam(value)) return;
        query.push(toSnakeCase(key), value);
      });
    }
  );

  const response = await wxApiClient.get(url);
  return response.data ?? { results: [], count: 0 };
};

export const getFieldForceTransactions = async (
  remote_utility_id: string,
  ff_no: string,
  page: number
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "wx",
    `job-card/action`,
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("field_force_no", ff_no);
      query.push("page", page);
    }
  );

  const response = await wxApiClient.get(url);
  return response.data ?? [];
};

export const getFieldForceActivityLogs = async (
  remote_utility_id: string,
  ff_id: string
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "activityLog",
    "logs",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("field_force_id", ff_id);
      query.push("module", "wx");
    }
  );

  const response = await wxApiClient.get(url);
  return response.data.results ?? [];
};

export const updatetatus = async (data: any): Promise<any> => {
  const payload = {
    code: data.code,
    is_active: data.is_active,
    remote_utility_id: data.remote_utility_id,
  };

  try {
    const response = await wxApiClient.put(
      `field-force-info/${data.id}/`,
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const updateFieldForce = async (
  formData: FormData,
  ff_no: any
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithPathVariables("wx", "field-force", [
    ff_no,
  ]);
  for (const [key, value] of formData.entries()) {
    if (value instanceof File) {
    } else {
    }
  }
  const response = await wxApiClient.put(url, formData);
  return response.data ?? null;
};

export const getAllCertificates = async (
  remote_utility_id: string,
  ff_id: any
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "wx",
    `field-force/detail/${ff_id}`,
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("field_name", "certificates");
    }
  );

  const response = await wxApiClient.get(url);
  return response.data.result ?? [];
};

export const getAllSkills = async (remote_utility_id: string, ff_id: any) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "wx",
    `field-force/detail/${ff_id}`,
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("field_name", "skills");
    }
  );

  const response = await wxApiClient.get(url);
  return response.data.result ?? [];
};

export const getAllAvailability = async (
  remote_utility_id: string,
  ff_id: any
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "wx",
    `field-force/detail/${ff_id}`,
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("field_name", "availability");
    }
  );

  const response = await wxApiClient.get(url);
  return response.data.result ?? [];
};

export const getSkills = async (remote_utility_id: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "wx",
    "skills",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("is_status", 1);
    }
  );

  const response = await wxApiClient.get(url);
  return response.data.result ?? [];
};

export const getCertificates = async (remote_utility_id: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/certificates",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const addCertificates = async (data: any, ff_id: any): Promise<any> => {
  const payload = {
    certificate: data.certificate,
    is_certificate: data.is_certificate,
    field_force_id: ff_id,
  };
  try {
    const response = await wxApiClient.post(
      `field-force-quick-service/certificates/`,
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const addSkills = async (data: any, ff_id: any): Promise<any> => {
  const payload = {
    skill_id: Number(data.skill),
    is_skill: data.is_skill,
    field_force_id: ff_id,
  };
  try {
    const response = await wxApiClient.post(
      `field-force-quick-service/skill/`,
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const getAreas = async (remote_utility_id: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/territory_json",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("config_level", "area");
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const getSubAreas = async (
  remote_utility_id: string,
  code_list: any[]
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/territory_json",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("config_level", "sub_area");
      for (const code of code_list) {
        query.push("code_list", code);
      }
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const updateOtherDetails = async (data: any): Promise<any> => {
  const payload = {
    additional_data: {
      normal_pay: data?.normal_pay,
      overtime_pay: data?.overtime_pay,
      certificates: data?.certificates,
    },
    remote_utility_id: data.remote_utility_id,
    area: data.area,
    sub_area: data.subArea,
    is_field_force_info: data.is_field_force_info,
    status: data.status,
    skills: data.skills,
  };

  try {
    const response = await wxApiClient.put(
      `field-force/${data.ff_no}/`,
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const updateAvailabilitySchedule = async (data: any): Promise<any> => {
  const payload = {
    additional_data: {
      availability_schedule: data?.additional_data?.availability_schedule,
    },
    remote_utility_id: data.remote_utility_id,
    is_field_force_info: data.is_field_force_info,
  };

  try {
    const response = await wxApiClient.put(
      `field-force/${data.ff_no}/`,
      payload
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

const getRates = async (
    utilityId: string,
    searchQuery?: string
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "field-force-rate",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
        if (searchQuery && searchQuery.trim()) {
          query.push("search_data", searchQuery.trim());
        }
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  }

  const getChoices = async (
    choice: string,
  ) => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "wx",
      "choices",
      [choice],
    );
    const response = await wxApiClient.get(url);
    return response.data;
  }

  export const updateFieldForceActiveStatus = async (ffNo: any, payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      'wx',
      'update-field-force-access',
      [ffNo]
    );
    const response  = wxApiClient.put(url, payload);
};

export const verifyEmail = async ( payload: any) => {
    const url = ApiEndpoints.createUrl(
      'wx',
      'verify-email',
    );
    const response = await wxApiClient.put(url, payload);
    return response.data;
};

export const getTemplates = async (
  channelType: string,
  remoteUtilityId: string
): Promise<any[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "communication",
    "get-templates",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("channel_type", channelType);
      query.push("is_active", 1);
    }
  );

  const response = await communicationApiClient.get(url);
  return response?.data?.result;
};

export const sendCommunication = async (data: any): Promise<any> => {
  const response = await communicationApiClient.post(
    ApiEndpoints.createUrl("communication", "direct-message"),
    data
  );
  return response.data;
};

export const api = {
  dashboard: {
    summary: getFieldForceSummary,
  },
  update: {
    updateFieldForce: updateFieldForce,
    getSkills: getSkills,
    getCertificates: getCertificates,
    getAreas: getAreas,
    getSubAreas: getSubAreas,
    updateOtherDetails: updateOtherDetails,
    updateAvailabilitySchedule: updateAvailabilitySchedule,
    updateFieldForceActiveStatus: updateFieldForceActiveStatus
  },
  list: {
    getList: getFieldForceData,
    updateStatus: updatetatus,
    exportData: exportFieldForceData,
    getRates: getRates,
    getChoices: getChoices
  },
  view: {
    getDetails: getFieldForceDetails,
    getAssignments: getFieldForceAssignments,
    getTransactions: getFieldForceTransactions,
    getActivityLogs: getFieldForceActivityLogs,
    getAllCertificates: getAllCertificates,
    getAllSkills: getAllSkills,
    getAllAvailability: getAllAvailability,
  },
  create: {
    addCertificates: addCertificates,
    addSkills: addSkills,
  },
};
